import { useState, useEffect, useCallback, useRef } from "react";
import { ref, onValue, off, set } from "firebase/database";
import { database } from "../constants/firebase";

export const useTypingStatus = (chatType, chatId, currentUser, otherUserId = null) => {
    const [isTyping, setIsTyping] = useState(chatType === 'group' ? {} : false);
    const typingTimeoutRef = useRef(chatType === 'group' ? {} : null);

    // Memoize these values to prevent unnecessary re-renders
    const currentUserUid = currentUser?.uid;
    const chatIdRef = useRef(chatId);
    const otherUserIdRef = useRef(otherUserId);

    // Update refs when values change
    useEffect(() => {
        chatIdRef.current = chatId;
    }, [chatId]);

    useEffect(() => {
        otherUserIdRef.current = otherUserId;
    }, [otherUserId]);

    // Path for LISTENING to others' typing status - now uses refs
    const getListenPath = useCallback((userId) => {
        switch (chatType) {
            case 'private':
                return `Users/${currentUserUid}/typingstatus/${chatIdRef.current}/${userId}`;
            case 'group':
                return `Users/${userId}/typingstatus/groups/${chatIdRef.current}/${userId}`;
            case 'community':
                return null;
            default:
                return null;
        }
    }, [chatType, currentUserUid]); // Only depend on chatType and currentUserUid

    // Path for UPDATING my own typing status - now uses refs
    const getUpdatePath = useCallback(() => {
        switch (chatType) {
            case 'private':
                return `Users/${otherUserIdRef.current}/typingstatus/${chatIdRef.current}/${currentUserUid}`;
            case 'group':
                return `Users/${currentUserUid}/typingstatus/groups/${chatIdRef.current}/${currentUserUid}`;
            case 'community':
                return null;
            default:
                return null;
        }
    }, [chatType, currentUserUid]); // Only depend on chatType and currentUserUid

    // Listen for typing status from others
    useEffect(() => {
        if (!chatIdRef.current || !currentUserUid || chatType === 'community') return;

        // For private chat - listen to the other user's typing status
        if (chatType === 'private' && otherUserIdRef.current) {
            const listenPath = getListenPath(otherUserIdRef.current);

            const typingRef = ref(database, listenPath);

            const unsubscribe = onValue(typingRef, snapshot => {
                const data = snapshot.val();

                if (data?.status && Date.now() - data.timestamp < 4000) {
                    setIsTyping(true);
                } else {
                    setIsTyping(false);
                }
            });

            return () => off(typingRef);
        }

        // For group chat - listen to all members
        if (chatType === 'group' && otherUserIdRef.current && Array.isArray(otherUserIdRef.current)) {
            const memberIds = otherUserIdRef.current;

            const unsubscribers = memberIds.map(memberId => {
                const listenPath = getListenPath(memberId);
                const typingRef = ref(database, listenPath);

                return onValue(typingRef, (snapshot) => {
                    const typingData = snapshot.val();
                    const now = Date.now();

                    setIsTyping(prev => {
                        // Create new object only if there's an actual change
                        const hasChanged =
                            (typingData?.status && now - typingData.timestamp < 4000)
                                ? !prev[memberId]
                                : prev[memberId];

                        if (!hasChanged) return prev;

                        const newStatus = { ...prev };
                        if (typingData?.status && now - typingData.timestamp < 4000) {
                            newStatus[memberId] = true;
                        } else {
                            delete newStatus[memberId];
                        }
                        return newStatus;
                    });
                });
            });

            return () => unsubscribers.forEach(unsubscribe => unsubscribe());
        }
    }, [chatType, currentUserUid, getListenPath]); // Remove otherUserId from deps, use refs instead

    // Update my own typing status
    const updateTypingStatus = useCallback((isCurrentlyTyping) => {
        if (!chatIdRef.current || !currentUserUid || chatType === 'community') return;

        const updatePath = getUpdatePath();
        if (!updatePath) return;

        const typingRef = ref(database, updatePath);

        if (isCurrentlyTyping) {
            // Set typing status
            set(typingRef, {
                status: true,
                timestamp: Date.now(),
            });

            // Clear existing timeout
            if (chatType === 'private' && typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            } else if (chatType === 'group' && typingTimeoutRef.current[chatIdRef.current]) {
                clearTimeout(typingTimeoutRef.current[chatIdRef.current]);
            }

            // Set new timeout to clear typing status after 3 seconds
            const timeoutId = setTimeout(() => {
                set(typingRef, null);
                if (chatType === 'private') {
                    typingTimeoutRef.current = null;
                } else {
                    delete typingTimeoutRef.current[chatIdRef.current];
                }
            }, 3000);

            if (chatType === 'private') {
                typingTimeoutRef.current = timeoutId;
            } else {
                typingTimeoutRef.current[chatIdRef.current] = timeoutId;
            }
        } else {
            // Clear typing status
            set(typingRef, null);

            if (chatType === 'private' && typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = null;
            } else if (chatType === 'group' && typingTimeoutRef.current[chatIdRef.current]) {
                clearTimeout(typingTimeoutRef.current[chatIdRef.current]);
                delete typingTimeoutRef.current[chatIdRef.current];
            }
        }
    }, [chatType, currentUserUid, getUpdatePath]);

    return { isTyping, updateTypingStatus };
};